import type { AdminMetricsDto } from '@carcheck/shared';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { GmvTile } from './GmvTile';
import { LockedTile } from './LockedTile';
import { OnlineNowTile } from './OnlineNowTile';
import { OrdersByHourTile } from './OrdersByHourTile';
import { OrderStructureTile } from './OrderStructureTile';
import { RequirePermission } from '../../../auth';

/**
 * The four `view_analytics`-gated analytics tiles (online-now, GMV,
 * order-structure donut, orders-by-hour) fed from ONE `GET /admin/metrics`
 * fetch. Each tile is individually gated so its grid slot survives — an
 * admin without `view_analytics` sees a `LockedTile` placeholder in the
 * same position, never a fabricated number.
 *
 * `RequirePermission` renders a transparent Fragment, so each tile stays a
 * DIRECT grid child of the dashboard grid (placement preserved).
 */
export interface AnalyticsSectionProps {
  metrics: AdminMetricsDto | undefined;
  isLoading: boolean;
  isError: boolean;
}

export const AnalyticsSection = ({
  metrics,
  isLoading,
  isError,
}: AnalyticsSectionProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <>
      <RequirePermission
        perm="view_analytics"
        fallback={<LockedTile label={t('dashboard.online.title')} gridColumn="span 3" />}
      >
        <OnlineNowTile data={metrics} isLoading={isLoading} isError={isError} gridColumn="span 3" />
      </RequirePermission>

      <RequirePermission
        perm="view_analytics"
        fallback={<LockedTile label={t('dashboard.gmv.title')} gridColumn="span 6" gridRow="span 2" />}
      >
        <GmvTile data={metrics} isLoading={isLoading} isError={isError} gridColumn="span 6" gridRow="span 2" />
      </RequirePermission>

      <RequirePermission
        perm="view_analytics"
        fallback={<LockedTile label={t('dashboard.donut.title')} gridColumn="span 3" gridRow="span 2" />}
      >
        <OrderStructureTile data={metrics} isLoading={isLoading} isError={isError} gridColumn="span 3" gridRow="span 2" />
      </RequirePermission>

      <RequirePermission
        perm="view_analytics"
        fallback={<LockedTile label={t('dashboard.orders.title')} gridColumn="span 3" />}
      >
        <OrdersByHourTile data={metrics} isLoading={isLoading} isError={isError} gridColumn="span 3" />
      </RequirePermission>
    </>
  );
};
